import {
  ArtifactStateMark, Button, CitationMarker, Composer, EvidenceLink, JumpLinks, KnowledgeItem, PersonList, ProblemBox,
  SegmentedControl, SourcesList, TopicRow,
} from "@don/design-system";
import type { KnowledgeKind, MemoryFact, PersonId, SessionId } from "@don/wire";
import type * as React from "react";
import { Fragment, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";

import { atOf, KIND_WORD } from "../backend/adapter.js";
import { calendarDateLabel, longDateLabel, type LibraryRow } from "../library/row.js";
import { knowledgePath, navigate } from "../routing/route.js";
import { atText, type StepAt } from "../session/reading.js";
import { firstName, orderRoster, type TeamSource } from "../settings/team.js";
import {
  citationLabel, linkOf, momentLabel, numberSources, SECTION_KINDS, sortTopics, type AnswerPart, type Asker, type ConversationStore,
  type LibraryView, type MemorySource, type Turn,
} from "./memory.js";
import {
  groupChanged, kindFailed, kindMore, kindRetry, kindSettled, more, NOBODY, pageFailed, pageSettled, personChanged,
  retry as retryPage, selectable, topicClosed, topicOpened, topicsFailed, topicsRetried, topicsSettled, type FactsState,
  type Group, type KindSections, type MemoryView, type RosterRead, type Step,
} from "./view.js";

/** knowledge-page design-spec §6 copy. */
export const ROSTER_LOADING = "Loading your team";
export const ROSTER_FAILED = "Couldn't load your team.";
export const CHOOSE_A_PERSON = "Choose a person";
export const MEMORY_LOADING = "Loading knowledge";
export const NO_PERSON_PLACEHOLDER = "Choose a person to ask about their work";
export const NO_MEMORY = "No knowledge yet.";
export const memoryFailed = (first: string) => `Couldn't load ${first}'s knowledge.`;
export const FACTS_LOADING = "Loading";
export const SHOW_MORE = "Show more";
export const SHOW_MORE_LOADING = "Loading…";
export const MORE_FAILED = "Couldn't load more.";
export const TOPIC_FAILED = "Couldn't load this topic.";
export const SECTION_FAILED = "Couldn't load this section.";
export const ANSWERING = "Answering…";
export const ANSWER_FAILED = "The answer didn't finish.";
export const RECORDING_DELETED = "recording deleted";

/** Where a moment links, or `null` when its recording is deleted (`linkOf`). */
type LinkFor = (sessionId: SessionId, at: StepAt) => string | null;

/** The memory header's toggle (design-spec §2, §6, D35): Topic by default, Type issue 281's. */
const GROUP_BY = [
  { id: "topic", label: "Topic" },
  { id: "type", label: "Type" },
] as const;

/**
 * The Knowledge page (epic 26-10-11-knowledge-page-v0, issue 277; design-spec §4.1): three
 * columns on the ground, no `PageFrame` (ux-spec D24), each scrolling on its own.
 *
 * - **Team** (left): the roster from the same `TeamSource.read()` the Team table uses, in
 *   `orderRoster` order, as a `PersonList` whose rows link to `/knowledge/{id}`. Loading is the
 *   pending mark, a failed read the ProblemBox with Try again.
 * - **Memory** (middle): the selected person's name as an `h1` (focused when the person was
 *   chosen by keyboard, D44), "Deactivated" beside it for a deactivated person, and the
 *   Topic | Type toggle (issue 281; page state, kept across people, `view.ts` `groupChanged`).
 *   Under it: the pending mark while their topics are outstanding; by Topic, their topics newest
 *   first (`sortTopics`), each a closed `TopicRow` dated by `longDateLabel`, one open at a time
 *   with its facts (issue 279, `Facts`); by Type, the kind sections (`KindView`);
 *   "No knowledge yet." when they have
 *   none; a failed read (a 404 included) as the ProblemBox with Try again, in this column only
 *   (issue 278). A long list scrolls inside the sheet (D42). With nobody selected it reads
 *   "Choose a person". Which person is selected, and which reads fire,
 *   is `view.ts`: an id the read roster lacks selects nobody and reads nothing (D13).
 * - **Ask** (right): the heading, the selected person's conversation (`Conversation`, issue
 *   282) and the composer, disabled with nobody selected and busy while their answer arrives.
 *   The conversation lives in `conversations`, the app's store, under `person:<id>`. While the
 *   roster is loading or failed the heading and composer are hidden.
 *
 * **Recording deleted** (issue 285, design-spec §9.5): the page reads the library (`library`)
 * once on entering, and at most once more, never with a read in flight, when a shown fact or
 * citation names a session the last answered read lacks and was first shown after that read was
 * sent. A moment or citation is unlinked only by `linkOf`; a failed re-read keeps the last
 * answered read. A session's `shownAtMs` is the first time this screen showed it, so its fact
 * and its citation read the same.
 */
export function KnowledgeScreen({
  team,
  memory,
  conversations,
  personId,
  nowMs,
  library,
  now,
}: {
  team: TeamSource;
  memory: MemorySource;
  /** The page's Ask conversations, which outlive this screen (`createConversationStore`). */
  conversations: ConversationStore;
  /** The page's clock, for the topic dates (`longDateLabel`). */
  nowMs: number;
  /** The URL's person (`/knowledge/{id}`), or `null` on `/knowledge`. */
  personId: PersonId | null;
  /** One library read (`Backend.library`, or the rows a test handed in). */
  library: () => Promise<readonly LibraryRow[]>;
  /** The Console's clock for the library stamps: strictly increasing milliseconds. */
  now: () => number;
}) {
  const [roster, setRoster] = useState<RosterRead>({ kind: "loading" });
  useEffect(() => {
    let live = true;
    setRoster({ kind: "loading" });
    team.read().then(
      (read) => live && setRoster({ kind: "loaded", roster: read }),
      () => live && setRoster({ kind: "failed", retrying: false }),
    );
    return () => {
      live = false;
    };
  }, [team]);

  async function retry() {
    if (roster.kind !== "failed" || roster.retrying) return;
    setRoster({ kind: "failed", retrying: true });
    try {
      setRoster({ kind: "loaded", roster: await team.read() });
    } catch {
      setRoster({ kind: "failed", retrying: false });
    }
  }

  // The memory column's state lives in a ref and is redrawn by a counter, so a re-run effect
  // (StrictMode) reads the selection it already made and asks nothing twice (`personChanged`).
  // A settle goes through `view.ts`, which drops one whose tag is not the current selection's.
  // Render only reads `view.current`; only `apply` (from an effect or a handler) writes it.
  const view = useRef<MemoryView>(NOBODY);
  const [, redraw] = useState(0);
  const source = useRef<MemorySource | null>(null);
  const apply = (step: Step) => {
    view.current = step.view;
    redraw((n) => n + 1);
    for (const read of step.reads) {
      const { tag } = read;
      if (read.method === "topics") {
        memory.topics(tag.personId).then(
          (topics) => apply({ view: topicsSettled(view.current, tag, topics), reads: [] }),
          () => apply({ view: topicsFailed(view.current, tag), reads: [] }),
        );
      } else if (read.scope === "kind") {
        memory.facts(tag.personId, read.query).then(
          (page) => apply({ view: kindSettled(view.current, read.key, tag, page), reads: [] }),
          () => apply({ view: kindFailed(view.current, read.key, tag), reads: [] }),
        );
      } else {
        memory.facts(tag.personId, read.query).then(
          (page) => apply({ view: pageSettled(view.current, read.key, tag, page), reads: [] }),
          () => apply({ view: pageFailed(view.current, read.key, tag), reads: [] }),
        );
      }
    }
  };
  useEffect(() => {
    // A newly handed memory source starts the column over, so the person is read from it.
    if (source.current !== memory) {
      source.current = memory;
      // The group is the page's, not the source's (D35).
      view.current = { ...NOBODY, seq: view.current.seq + 1, group: view.current.group };
    }
    apply(personChanged(view.current, roster, personId));
    // `apply` closes over this render's `memory`, which is listed below.
  }, [memory, roster, personId]);

  // The library as last answered, and the reads this visit has sent (issue 285).
  const [libraryView, setLibraryView] = useState<LibraryView>({ state: "reading" });
  const libraryReads = useRef({ sent: 0, inFlight: false });
  const shownAt = useRef(new Map<SessionId, number>());
  function readLibrary() {
    const reads = libraryReads.current;
    reads.sent += 1;
    reads.inFlight = true;
    const sentAtMs = now();
    library().then(
      (rows) => {
        reads.inFlight = false;
        // A recording's row stands for every segment, each a session a fact may cite (issue 321).
        const sessionIds = new Set(rows.flatMap((r) => (r.segments ?? [r]).map((each) => each.sessionId)));
        setLibraryView({ state: "read", sessionIds, sentAtMs, answeredAtMs: now() });
      },
      () => {
        reads.inFlight = false;
        // A failed re-read keeps the last answer; a failed first read leaves every link live.
        setLibraryView((was) => (was.state === "read" ? was : { state: "failed" }));
      },
    );
  }
  // Once per visit, a ref so StrictMode's re-run effect sends nothing twice.
  useEffect(() => {
    if (libraryReads.current.sent === 0) readLibrary();
    // `readLibrary` reads this render's `library`; the visit's first is the one asked.
  }, []);
  const linkFor: LinkFor = (sessionId, at) => linkOf(sessionId, at, libraryView, shownAt.current.get(sessionId) ?? Infinity);

  const person = selectable(roster, view.current.person);
  const topics = view.current.topics;
  const rosterRead = roster.kind === "loaded";

  // Chosen by keyboard: once that person's header is drawn, focus it (D44).
  const focusFor = useRef<PersonId | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focusFor.current === null || person?.person_id !== focusFor.current) return;
    heading.current?.focus();
    focusFor.current = null;
  });

  // A pressed Show more or Try again leaves the page (it turns disabled or is replaced), so once
  // its read settles, focus goes back to where the reader was if nothing else took it: the
  // memory header or the topics' Try again, and in an open topic its Show more, its Try again,
  // or the open row (278 UX P1-1, design-spec §5.1 default focus).
  // A kind section's Show more or Try again does the same inside its section, then its heading.
  const refocus = useRef<"topics" | "facts" | { kind: KnowledgeKind } | null>(null);
  const memoryColumn = useRef<HTMLElement>(null);
  useEffect(() => {
    const want = refocus.current;
    if (want === null) return;
    const now = view.current;
    const facts = want === "facts" ? now.open?.facts : want === "topics" ? undefined : now.kinds?.[want.kind].facts;
    if (want === "topics" ? now.topics.kind === "failed" && now.topics.retrying : facts !== undefined && waiting(facts)) return;
    refocus.current = null;
    const active = document.activeElement;
    const lost = !(active instanceof HTMLElement) || active === document.body || !active.isConnected || active.matches(":disabled");
    const column = memoryColumn.current;
    if (!lost || column === null) return;
    const within = (scope: string) =>
      column.querySelector<HTMLElement>(`${scope} [data-knowledge-more], ${scope} [data-problem-box] button`);
    const target =
      want === "topics"
        ? (column.querySelector<HTMLElement>('[data-knowledge-memory-state="failed"] button') ?? heading.current)
        : want === "facts"
          ? (within("[data-topic-group]") ?? column.querySelector<HTMLElement>('[data-topic-row][aria-expanded="true"]'))
          : (within(`[data-knowledge-kind="${want.kind}"]`) ??
            column.querySelector<HTMLElement>(`[data-knowledge-kind="${want.kind}"] h3`));
    target?.focus();
  });

  // By Type, the inner column gets room under the last section so a jump can bring any section
  // to the top (design-spec §4.3 "Type bottom room"); measured after every render.
  const inner = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const column = memoryColumn.current;
    const box = inner.current;
    if (column === null || box === null) return;
    const index = column.querySelector<HTMLElement>("[data-jump-links]");
    const sections = column.querySelectorAll<HTMLElement>("[data-knowledge-kind]");
    const last = sections[sections.length - 1];
    box.style.paddingBottom =
      index === null || last === undefined
        ? ""
        : // knowledge-page design-spec §4.3: the sheet's 64px bottom padding.
          `${Math.max(0, column.clientHeight - index.offsetHeight - last.offsetHeight - 64)}px`;
  });

  // A jump link: the memory column scrolls, instantly, so the section's top meets the index's
  // bottom, and its heading takes focus (design-spec §2, §7).
  function jump(kind: string) {
    const column = memoryColumn.current;
    const section = column?.querySelector<HTMLElement>(`[data-knowledge-kind="${kind}"]`);
    const index = column?.querySelector<HTMLElement>("[data-jump-links]");
    if (column == null || section == null || index == null) return;
    // Against where the index sticks (the column's top), not where it sits now: before a first
    // jump it still rides under the header.
    column.scrollTop += section.getBoundingClientRect().top - (column.getBoundingClientRect().top + column.clientTop + index.offsetHeight);
    section.querySelector<HTMLElement>("h3")?.focus({ preventScroll: true });
  }

  // The composer's draft belongs to one person and is gone on switching (design-spec §9.7).
  // Reset in render when the shown person changes, so it does not come back on returning (283).
  const [draft, setDraft] = useState<{ person: PersonId | null; text: string }>({ person: null, text: "" });
  const shownId = person?.person_id ?? null;
  if (draft.person !== shownId) setDraft({ person: shownId, text: "" });
  const draftText = draft.person === shownId ? draft.text : "";

  // The selected person's conversation (issue 282), redrawn on every change to the store.
  const turnsByScope = useSyncExternalStore(conversations.subscribe, conversations.snapshot);
  const scope = person === null ? null : `person:${person.person_id}`;
  const turns = scope === null ? [] : (turnsByScope.get(scope) ?? []);
  const answering = scope !== null && conversations.streaming(scope);
  // Stamp each session the first time it is shown, then re-read once if the library lacks one
  // shown after its last read was sent.
  useEffect(() => {
    const shown = view.current;
    const kinds = shown.group === "type" ? shown.kinds : null;
    const facts = [shown.open?.facts, ...(kinds === null ? [] : SECTION_KINDS.map((k) => kinds[k].facts))];
    const ids = [
      ...facts.flatMap((f) => (f?.kind === "read" ? f.facts.map((x) => x.session_id) : [])),
      ...turns.flatMap((t) => (t.role === "assistant" ? t.parts.flatMap((p) => p.citations.map((c) => c.session_id)) : [])),
    ];
    for (const id of ids) if (!shownAt.current.has(id)) shownAt.current.set(id, now());
    const reads = libraryReads.current;
    if (libraryView.state !== "read" || reads.inFlight || reads.sent >= 2) return;
    const lib = libraryView;
    if (ids.some((id) => !lib.sessionIds.has(id) && shownAt.current.get(id)! > lib.sentAtMs)) readLibrary();
  });
  const askerFor = (id: PersonId): Asker => (request, signal) => memory.ask(id, request, signal);
  function sendQuestion() {
    if (person === null || scope === null) return;
    const id = person.person_id;
    if (conversations.send(scope, draftText, askerFor(id))) {
      setDraft({ person: id, text: "" });
    }
  }
  // Try again under a failed last answer (D20, issue 284). The button leaves the page with the
  // turn it sat under, so focus goes to the question field.
  const composerBox = useRef<HTMLDivElement>(null);
  function retryAnswer() {
    if (person === null || scope === null) return;
    if (conversations.retry(scope, askerFor(person.person_id))) composerBox.current?.querySelector("textarea")?.focus();
  }

  return (
    <div
      data-knowledge=""
      style={{
        flex: 1,
        minHeight: 0,
        display: "grid",
        // knowledge-page design-spec §3.3: 240px and 400px columns.
        gridTemplateColumns: "240px minmax(0, 1fr) 400px",
        columnGap: "var(--space-15)",
        padding: "var(--space-15) var(--space-15) var(--space-15) 0",
        background: "var(--ground)",
      }}
    >
      <div
        data-knowledge-people=""
        // knowledge-page design-spec §4.1: person column padding `22px 12px 24px 16px`.
        style={{ minHeight: 0, overflow: "auto", padding: "22px var(--space-15) var(--space-3) var(--space-2)" }}
      >
        {/* knowledge-page design-spec §4.2: padding `0 0 14px 14px`. */}
        <h2 style={{ ...COLUMN_HEADING, padding: "0 0 14px 14px" }}>Team</h2>
        {roster.kind === "loading" ? (
          <div data-knowledge-roster="loading" style={{ paddingLeft: "14px", fontSize: "var(--type-small-size)" }}>
            <ArtifactStateMark state="pending">{ROSTER_LOADING}</ArtifactStateMark>
          </div>
        ) : roster.kind === "failed" ? (
          <div data-knowledge-roster="failed">
            <ProblemBox onRetry={() => void retry()} retryBusy={roster.retrying}>
              <b style={{ fontWeight: "var(--type-body-weight-current)" }}>{ROSTER_FAILED}</b> The server didn't answer.
            </ProblemBox>
          </div>
        ) : (
          <PersonList
            label="Team"
            current={person?.person_id ?? null}
            people={orderRoster(roster.roster.people).map((m) => ({
              id: m.person_id,
              name: m.display_name,
              deactivated: m.status === "deactivated",
              href: knowledgePath(m.person_id),
            }))}
            onSelect={(id, byKeyboard) => {
              if (byKeyboard) focusFor.current = id;
              if (id === personId) redraw((n) => n + 1);
              else navigate(knowledgePath(id));
            }}
          />
        )}
      </div>

      <section ref={memoryColumn} data-knowledge-memory="" aria-label="Knowledge" style={{ ...SHEET, padding: "0 var(--space-6) 64px" }}>
        <div ref={inner} style={{ maxWidth: "var(--reading-measure)", margin: "0 auto" }}>
          {!rosterRead ? null : person === null ? (
            <p data-knowledge-choose="" style={{ margin: 0, paddingTop: "var(--space-5)", color: "var(--ink-2)" }}>
              {CHOOSE_A_PERSON}
            </p>
          ) : (
            <>
              <header
                data-knowledge-head={person.person_id}
                style={{
                  display: "flex", alignItems: "center", gap: "var(--space-15)",
                  paddingTop: "var(--space-5)", marginBottom: "var(--space-3)",
                }}
              >
                <h1
                  ref={heading}
                  tabIndex={-1}
                  data-knowledge-name=""
                  style={{
                    margin: 0,
                    fontSize: "var(--type-title-size)",
                    lineHeight: "var(--type-title-line)",
                    fontWeight: "var(--type-title-weight)",
                    letterSpacing: "var(--type-title-tracking)",
                    borderRadius: "var(--radius-focus)",
                  }}
                >
                  {person.display_name}
                </h1>
                {person.status === "deactivated" && (
                  <span
                    data-knowledge-deactivated=""
                    // knowledge-page design-spec §4.3: `padding-bottom: 3px`.
                    style={{ alignSelf: "flex-end", paddingBottom: "3px", color: "var(--ink-2)", fontSize: "var(--type-small-size)" }}
                  >
                    Deactivated
                  </span>
                )}
                <div style={{ marginLeft: "auto" }}>
                  <SegmentedControl
                    label="Group by"
                    options={GROUP_BY}
                    value={view.current.group}
                    onChange={(id) => apply(groupChanged(view.current, id as Group))}
                  />
                </div>
              </header>
              {topics.kind === "loading" ? (
                <div data-knowledge-memory-state="loading">
                  <ArtifactStateMark state="pending">{MEMORY_LOADING}</ArtifactStateMark>
                </div>
              ) : topics.kind === "failed" ? (
                <div data-knowledge-memory-state="failed">
                  <ProblemBox
                    onRetry={() => {
                      refocus.current = "topics";
                      apply(topicsRetried(view.current));
                    }}
                    retryBusy={topics.retrying}
                  >
                    <b style={{ fontWeight: "var(--type-body-weight-current)" }}>{memoryFailed(firstName(person))}</b> The
                    server didn't answer.
                  </ProblemBox>
                </div>
              ) : topics.kind === "read" && view.current.group === "type" ? (
                <KindView
                  kinds={view.current.kinds}
                  linkFor={linkFor}
                  onJump={jump}
                  onMore={(kind) => {
                    refocus.current = { kind };
                    apply(kindMore(view.current, kind));
                  }}
                  onRetry={(kind) => {
                    refocus.current = { kind };
                    apply(kindRetry(view.current, kind));
                  }}
                />
              ) : topics.kind === "read" && topics.topics.topics.length === 0 ? (
                <div data-knowledge-memory-state="absent">
                  <ArtifactStateMark state="absent">{NO_MEMORY}</ArtifactStateMark>
                </div>
              ) : topics.kind === "read" ? (
                <ul
                  data-knowledge-topics=""
                  aria-label="Topics"
                  style={{ listStyle: "none", margin: 0, padding: 0, borderTop: "1px solid var(--line)" }}
                >
                  {sortTopics(topics.topics.topics).map((topic) => {
                    const open = view.current.open?.key === topic.topic ? view.current.open : null;
                    return (
                      <TopicRow
                        key={topic.topic}
                        name={topic.topic}
                        date={longDateLabel(nowMs, topic.last_added_at_ms)}
                        open={open !== null}
                        onToggle={() =>
                          open !== null ? apply({ view: topicClosed(view.current), reads: [] }) : apply(topicOpened(view.current, topic.topic))
                        }
                        // The group's id is the topic's, not its place in the list (278 code-quality P2-1).
                        controls={`knowledge-topic-${encodeURIComponent(topic.topic)}`}
                      >
                        {open !== null && (
                          <Facts
                            by="topic"
                            linkFor={linkFor}
                            facts={open.facts}
                            onMore={() => {
                              refocus.current = "facts";
                              apply(more(view.current, open.key));
                            }}
                            onRetry={() => {
                              refocus.current = "facts";
                              apply(retryPage(view.current, open.key));
                            }}
                          />
                        )}
                      </TopicRow>
                    );
                  })}
                </ul>
              ) : null}
            </>
          )}
        </div>
      </section>

      <section
        data-knowledge-ask=""
        aria-label="Ask"
        style={{ ...SHEET, overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        {/* knowledge-page design-spec §4.4: padding `22px 28px 6px`. */}
        <h2
          data-knowledge-ask-heading=""
          style={{ ...COLUMN_HEADING, padding: "22px var(--space-35) var(--space-075)", visibility: rosterRead ? undefined : "hidden" }}
        >
          Ask
        </h2>
        <Conversation scope={scope} turns={turns} onRetry={retryAnswer} linkFor={linkFor} />
        <div ref={composerBox} data-knowledge-composer="" style={{ visibility: rosterRead ? undefined : "hidden" }}>
          <Composer
            value={draftText}
            onChange={(text) => setDraft({ person: shownId, text })}
            onSend={sendQuestion}
            placeholder={person === null ? NO_PERSON_PLACEHOLDER : `Ask about ${firstName(person)}'s work`}
            disabled={person === null}
            busy={answering}
          />
        </div>
      </section>
    </div>
  );
}

/**
 * The Ask conversation (issue 282; design-spec §2, §4.4, §7): each question as a bubble, each
 * answer as its sentences (`Answer`), in a column that is `aria-live="polite"`. When a part
 * arrives the column scrolls to the bottom only if it was within 24px of the bottom before, so
 * a reader who scrolled up keeps their place; a new question always scrolls to it.
 */
function Conversation({
  scope, turns, onRetry, linkFor,
}: { scope: string | null; turns: readonly Turn[]; onRetry(): void; linkFor: LinkFor }) {
  const box = useRef<HTMLDivElement>(null);
  // Whether the reader is at the bottom, as of their last scroll (design-spec §7: 24px).
  const pinned = useRef(true);
  const seen = useRef<{ scope: string | null; count: number }>({ scope, count: turns.length });
  // Which answers have their Sources open, by scope and turn index; all collapsed at first (D37).
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  useLayoutEffect(() => {
    const el = box.current;
    if (el === null) return;
    const fresh = seen.current.scope !== scope || turns.length > seen.current.count;
    seen.current = { scope, count: turns.length };
    if (fresh || pinned.current) {
      el.scrollTop = el.scrollHeight;
      pinned.current = true;
    }
  }, [scope, turns]);
  return (
    <div
      ref={box}
      data-knowledge-conversation=""
      aria-live="polite"
      onScroll={(event) => {
        const el = event.currentTarget;
        // knowledge-page design-spec §7: within 24px of the bottom counts as at it.
        pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 24;
      }}
      // knowledge-page design-spec §4.4: conversation padding `14px 28px 20px`, turns 20px apart.
      style={{
        flex: 1, minHeight: 0, overflow: "auto", padding: "14px var(--space-35) var(--space-25)",
        display: "flex", flexDirection: "column", gap: "var(--space-25)",
      }}
    >
      {turns.map((turn, i) =>
        turn.role === "owner" ? (
          <div
            key={i}
            data-owner-turn=""
            style={{
              alignSelf: "flex-end",
              // knowledge-page design-spec §3.3: 86% bubble width, `12px 12px 4px 12px` radius, `9px 14px` padding.
              maxWidth: "86%",
              background: "var(--tone)",
              borderRadius: "12px 12px 4px 12px",
              padding: "9px 14px",
              fontSize: "var(--type-body-size)",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {turn.text}
          </div>
        ) : (
          <Answer
            key={i}
            id={`knowledge-sources-${i}`}
            parts={turn.parts}
            state={turn.state}
            linkFor={linkFor}
            // Try again only under the last turn: once a newer question is asked, a failed
            // answer keeps its ProblemBox text without it (design-spec §5.3).
            onRetry={i === turns.length - 1 ? onRetry : undefined}
            open={open.has(`${scope}#${i}`)}
            onToggle={() =>
              setOpen((was) => {
                const next = new Set(was);
                const key = `${scope}#${i}`;
                if (!next.delete(key)) next.add(key);
                return next;
              })
            }
          />
        ),
      )}
    </div>
  );
}

/**
 * One answer (design-spec §4.4, §6; D21, D30, D38): its sentences joined by one space, a cited
 * one in ink-1 with its markers, an unsourced one (no citations) in ink-2 with none; a part with
 * no text is skipped. While it streams, the static "Answering…" line under it; once it ends, its
 * Sources when it has any. Nothing found is an answer like any other. A failed one keeps what
 * arrived, then the full-width ProblemBox, with Try again when `onRetry` is given (issue 284).
 */
function Answer({
  id, parts, state, open, onToggle, onRetry, linkFor,
}: {
  id: string;
  parts: readonly AnswerPart[];
  state: "streaming" | "done" | "failed";
  open: boolean;
  onToggle(): void;
  onRetry?: () => void;
  linkFor: LinkFor;
}) {
  const sources = numberSources(parts);
  const numberOf = new Map(sources.map((source) => [source.citation.fact_id, source.n]));
  const shown = parts.filter((part) => part.text.trim().length > 0);
  return (
    <div data-answer={state} style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
      {shown.length > 0 && (
        <p style={{ margin: 0, fontSize: "var(--type-prose-size)", lineHeight: "var(--type-prose-line)", overflowWrap: "anywhere" }}>
          {shown.map((part, i) => (
            <Fragment key={i}>
              {i > 0 && " "}
              <Sentence part={part} numberOf={numberOf} linkFor={linkFor} />
            </Fragment>
          ))}
        </p>
      )}
      {state === "streaming" ? (
        // knowledge-page design-spec §4.4: 14px, `margin-top: 2px`.
        <div data-answer-pending="" style={{ marginTop: "var(--space-025)", fontSize: "var(--type-small-size)" }}>
          <ArtifactStateMark state="pending">{ANSWERING}</ArtifactStateMark>
        </div>
      ) : sources.length > 0 ? (
        <SourcesList
          id={id}
          open={open}
          onToggle={onToggle}
          sources={sources.map(({ n, citation }) => {
            const at = atOf(citation.at);
            const href = linkFor(citation.session_id, at);
            const text = `${calendarDateLabel(citation.recorded_at_ms)} · ▶ ${atText(at)}`;
            // A gone row is plain ink-2 text, still numbered (design-spec §6).
            return { n, text: href === null ? `${text} · ${RECORDING_DELETED}` : text, href, label: momentLabel(at) };
          })}
        />
      ) : null}
      {state === "failed" && (
        // knowledge-page design-spec §4.4: the answer ProblemBox `margin-top: 6px`, full column width.
        <div data-answer-problem="" style={{ marginTop: "var(--space-075)" }}>
          <ProblemBox wide onRetry={onRetry}>
            <b style={{ fontWeight: "var(--type-body-weight-current)" }}>{ANSWER_FAILED}</b> The server stopped answering.
          </ProblemBox>
        </div>
      )}
    </div>
  );
}

/** A sentence and its markers; the last word and the markers never part across a line. */
function Sentence({ part, numberOf, linkFor }: { part: AnswerPart; numberOf: ReadonlyMap<string, number>; linkFor: LinkFor }) {
  const text = part.text.trim();
  const cited = part.citations.length > 0;
  const cut = text.lastIndexOf(" ") + 1;
  const markers = [...new Map(part.citations.map((c) => [c.fact_id, c])).values()];
  return (
    <span data-answer-sentence={cited ? "cited" : "unsourced"} style={{ color: cited ? "var(--ink-1)" : "var(--ink-2)" }}>
      {text.slice(0, cut)}
      <span style={{ whiteSpace: "nowrap" }}>
        {text.slice(cut)}
        {markers.map((citation) => {
          const n = numberOf.get(citation.fact_id)!;
          const at = atOf(citation.at);
          return <CitationMarker key={citation.fact_id} n={n} href={linkFor(citation.session_id, at)} label={citationLabel(n, at)} />;
        })}
      </span>
    </span>
  );
}

/** A page read is in flight for these facts: focus waits for it (`refocus`). */
const waiting = (facts: FactsState) =>
  facts.kind === "loading" || (facts.kind === "failed" && facts.retrying) || (facts.kind === "read" && facts.more === "loading");

/**
 * The Type view (issue 281; design-spec §4.3, §5, §6, ux-spec D36): the sticky `JumpLinks`
 * index ("Types"), then a section per kind in `KINDS` order, each an `h3` (sticky under the
 * index, the jump's focus target) over its own `Facts`. A kind whose first page came back
 * empty is not drawn and not in the index; a loading or failed one is both. Every kind empty:
 * "No knowledge yet." and no index.
 */
function KindView({
  kinds,
  linkFor,
  onJump,
  onMore,
  onRetry,
}: {
  kinds: KindSections | null;
  linkFor: LinkFor;
  onJump(kind: string): void;
  onMore(kind: KnowledgeKind): void;
  onRetry(kind: KnowledgeKind): void;
}) {
  if (kinds === null) return null;
  const shown = SECTION_KINDS.filter((kind) => {
    const facts = kinds[kind].facts;
    return facts.kind !== "read" || facts.facts.length > 0;
  });
  if (shown.length === 0) {
    return (
      <div data-knowledge-memory-state="absent">
        <ArtifactStateMark state="absent">{NO_MEMORY}</ArtifactStateMark>
      </div>
    );
  }
  return (
    <div data-knowledge-types="">
      <JumpLinks label="Types" items={shown.map((kind) => ({ id: kind, label: KIND_WORD[kind] }))} onJump={onJump} />
      {shown.map((kind, i) => (
        <section
          key={kind}
          data-knowledge-kind={kind}
          aria-label={KIND_WORD[kind]}
          // knowledge-page design-spec §4.3: section `padding: 20px 0 6px`, a rule above each after the first.
          style={{ padding: "var(--space-25) 0 var(--space-075)", borderTop: i === 0 ? undefined : "1px solid var(--line)" }}
        >
          <h3
            tabIndex={-1}
            style={{
              position: "sticky",
              // knowledge-page design-spec §3.3: the type heading sticks 45px down, under the index.
              top: "45px",
              zIndex: 1,
              margin: 0,
              padding: "var(--space-075) 0 10px",
              background: "var(--sheet)",
              fontSize: "var(--type-row-title-size)",
              lineHeight: 1.35,
              fontWeight: "var(--type-body-weight-current)",
              borderRadius: "var(--radius-focus)",
            }}
          >
            {KIND_WORD[kind]}
          </h3>
          <Facts by="type" linkFor={linkFor} facts={kinds[kind].facts} onMore={() => onMore(kind)} onRetry={() => onRetry(kind)} />
        </section>
      ))}
    </div>
  );
}

/**
 * One list of facts (issues 279, 281; design-spec §4.3, §5, §6): "Loading" while the first page
 * is out; a failed first page as the ProblemBox with Try again ("Couldn't load this topic." in
 * an open topic, "Couldn't load this section." in a kind section); the facts newest first
 * (`view.ts` sorts and merges), each a memory-form `KnowledgeItem` whose meta leads with the
 * kind by Topic and with the topic by Type; then Show more while a next page exists,
 * "Loading…" and disabled while it loads, or under the facts "Couldn't load more." with Try
 * again (the same page token) when it failed. By Type the gaps are 18px, by Topic 22px.
 */
function Facts({
  by, facts, linkFor, onMore, onRetry,
}: { by: Group; facts: FactsState; linkFor: LinkFor; onMore(): void; onRetry(): void }) {
  // knowledge-page design-spec §3.3 / §4.3: facts 22px apart by Topic, 18px by Type.
  const gap = by === "topic" ? "22px" : "18px";
  if (facts.kind === "loading") {
    return (
      <div data-knowledge-facts-state="loading" style={{ fontSize: "var(--type-small-size)" }}>
        <ArtifactStateMark state="pending">{FACTS_LOADING}</ArtifactStateMark>
      </div>
    );
  }
  if (facts.kind === "failed") {
    return (
      <div data-knowledge-facts-state="failed">
        <ProblemBox onRetry={onRetry} retryBusy={facts.retrying}>
          <b style={{ fontWeight: "var(--type-body-weight-current)" }}>{by === "topic" ? TOPIC_FAILED : SECTION_FAILED}</b> The
          server didn't answer.
        </ProblemBox>
      </div>
    );
  }
  return (
    <>
      <div data-knowledge-facts="" style={{ display: "flex", flexDirection: "column", gap }}>
        {facts.facts.map((fact) => (
          <KnowledgeItem key={fact.id} form="memory" data-fact={fact.id} statement={fact.statement} meta={<FactMeta fact={fact} by={by} href={linkFor(fact.session_id, atOf(fact.at))} />} />
        ))}
      </div>
      {facts.more === "failed" ? (
        <div data-knowledge-facts-state="more-failed" style={{ marginTop: gap }}>
          <ProblemBox onRetry={onRetry}>
            <b style={{ fontWeight: "var(--type-body-weight-current)" }}>{MORE_FAILED}</b> The server didn't answer.
          </ProblemBox>
        </div>
      ) : facts.next !== null ? (
        <div style={{ marginTop: gap }}>
          <Button variant="secondary" size="sm" data-knowledge-more="" disabled={facts.more === "loading"} onClick={onMore}>
            {facts.more === "loading" ? SHOW_MORE_LOADING : SHOW_MORE}
          </Button>
        </div>
      ) : null}
    </>
  );
}

/**
 * "<Kind> · <Sep 24, 2026> · ▶ <14:22>" by Topic, "<topic> · …" by Type, where the heading
 * already names the kind (design-spec §6, D36); the moment opens the session in a new tab. With
 * its recording deleted (`href` null) the moment keeps its ▶ in ink-2, unlinked, then
 * "· recording deleted" (issue 285).
 */
function FactMeta({ fact, by, href }: { fact: MemoryFact; by: Group; href: string | null }) {
  const at = atOf(fact.at);
  return (
    <>
      {by === "topic" ? (
        <span data-fact-kind="">{KIND_WORD[fact.kind] ?? "Other"}</span>
      ) : (
        <span data-fact-topic="">{fact.topic}</span>
      )}
      <Dot />
      <span data-fact-date="">{calendarDateLabel(fact.recorded_at_ms)}</span>
      <Dot />
      {href === null ? (
        <>
          <EvidenceLink time={atText(at)} off keepGlyph data-fact-moment="" />
          <Dot />
          <span data-fact-deleted="">{RECORDING_DELETED}</span>
        </>
      ) : (
        <EvidenceLink time={atText(at)} href={href} label={momentLabel(at)} data-fact-moment="" />
      )}
    </>
  );
}

const Dot = () => (
  <span aria-hidden="true" style={{ color: "var(--ink-3)" }}>
    ·
  </span>
);

const SHEET: React.CSSProperties = {
  minHeight: 0,
  overflow: "auto",
  background: "var(--sheet)",
  borderRadius: "var(--radius-sheet)",
  boxShadow: "var(--sheet-edge)",
};

const COLUMN_HEADING: React.CSSProperties = {
  margin: 0,
  color: "var(--ink-1)",
  fontSize: "var(--type-row-title-size)",
  // knowledge-page design-spec §3.3: Team/Ask heading line 1.3.
  lineHeight: 1.3,
  fontWeight: "var(--type-row-title-weight)",
};
